import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Label, PhoneInput } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { formatName, formatPhone } from "@tellescope/utils";
import { EnduserSearchCombobox, Enduser } from "@tellescope/contacts";
import { useForm } from "@tellescope/forms";
import { useCalling } from "../../hooks/calling";
import { usePhoneNumberLabel } from "../../hooks/calling/useTwilioNumbers";
import { useFromNumbers } from "../../hooks/calling/useFromNumbers";
import { useDefaultCallSettings } from "../../hooks/calling/useDefaultCallSettings";
import { pendo } from "../../tracking";

export interface DialerPanelProps {
  /** Callback when the panel is closed */
  onClose: () => void;
  /** Callback when a call is placed */
  onCallPlaced?: () => void;
  /** Optional enduser to pre-fill the dialer with */
  initialEnduser?: Enduser;
}

/**
 * DialerPanel - UI for initiating outbound phone calls.
 *
 * Provides:
 * - From field: Select dropdown with available Twilio numbers
 * - To field: Searchable enduser selector
 * - Call Type: Direct or Conference
 * - Action buttons: Cancel and Place call
 */
export function DialerPanel({ onClose, onCallPlaced, initialEnduser }: DialerPanelProps) {
  const { t } = useTranslation("calling");
  const calling = useCalling();

  // Get available numbers and defaults
  const { options: twilioNumbers } = useFromNumbers();
  const getPhoneLabel = usePhoneNumberLabel();
  const { defaultFromNumber, defaultCallType, recordCalls } = useDefaultCallSettings();

  // Selected enduser state
  const [selectedEnduser, setSelectedEnduser] = useState<Enduser[]>(initialEnduser ? [initialEnduser] : []);

  // Form state via useForm
  const dialerForm = useForm({
    fields: {
      fromNumber: {
        type: "select" as const,
        initialValue: defaultFromNumber,
        required: true,
      },
      callType: {
        type: "select" as const,
        initialValue: defaultCallType,
        required: true,
      },
      directPhoneNumber: {
        type: "text" as const,
        initialValue: "",
        required: false,
      },
    },
    onSubmit: async (values) => {
      // Use direct phone number if entered, otherwise use contact phone
      const enduser = selectedEnduser[0];
      const phoneToCall = values.directPhoneNumber || enduser?.phone;
      if (!phoneToCall) return;

      const conferenceId = values.callType === "Conference" ? crypto.randomUUID() : undefined;

      pendo.track("place-call-dialog-confirm");
      await calling.makeCall(phoneToCall, {
        // Only include enduserId if calling a contact (not direct number entry)
        enduserId: values.directPhoneNumber ? undefined : enduser?.id,
        enduserName: values.directPhoneNumber ? undefined : enduser ? formatName(enduser) : undefined,
        fromNumber: values.fromNumber,
        conferenceId,
        recordConference: recordCalls && !!conferenceId,
      });

      onCallPlaced?.();
    },
  });

  // Check if we can place a call
  const canPlaceCall =
    (!!selectedEnduser[0]?.phone || !!dialerForm.values.directPhoneNumber) &&
    !!dialerForm.values.fromNumber &&
    !dialerForm.isSubmitting &&
    calling.isConnected;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* From Number Field */}
      <div className="space-y-2">
        <Label htmlFor="from-number">{t("From")}</Label>
        <Select
          value={dialerForm.values.fromNumber}
          onValueChange={(value) => dialerForm.setFieldValue("fromNumber", value)}
        >
          <Select.Trigger id="from-number" className="h-9 w-full">
            <Select.Value placeholder={t("Select phone number")} />
          </Select.Trigger>
          <Select.Content>
            {twilioNumbers.map((number) => {
              const label = getPhoneLabel(number);
              return (
                <Select.Item key={number} value={number}>
                  {label !== number ? `${label}: ${formatPhone(number)}` : formatPhone(number)}
                </Select.Item>
              );
            })}
          </Select.Content>
        </Select>
      </div>

      {/* To Field - Enduser Search */}
      <div className="space-y-2">
        <Label>{t("To")}</Label>
        <EnduserSearchCombobox
          value={selectedEnduser}
          onChange={(endusers) => {
            setSelectedEnduser(endusers);
            // Clear direct phone number when selecting contact
            if (endusers.length > 0) {
              dialerForm.setFieldValue("directPhoneNumber", "");
            }
          }}
          defaultRendererVariant="phone"
          queryOptions={{
            requestOverrides: {
              mdbFilter: { phone: { $exists: true } },
            },
          }}
          customFilter={(searchTerm, options) => {
            const lower = searchTerm.toLowerCase();
            return options.filter((opt) => {
              const enduser = opt.metadata?.enduser;
              return (
                opt.label.toLowerCase().includes(lower) ||
                opt.value.toLowerCase().includes(lower) ||
                enduser?.phone?.includes(searchTerm) ||
                enduser?.email?.toLowerCase().includes(lower)
              );
            });
          }}
          formatSelectedValue={(endusers) => {
            const enduser = endusers[0];
            if (!enduser) return null;
            return (
              <p className="truncate">
                {formatName(enduser)}
                <span className="ml-1 text-muted-foreground">{formatPhone(enduser.phone!)}</span>
              </p>
            );
          }}
          placeholder={t("Search contacts...")}
          searchPlaceholder={t("Type to search...")}
          emptyText={t("No contacts found")}
          clearable
        />
      </div>

      {/* "or" Divider */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">{t("or")}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Direct Phone Number Entry */}
      <PhoneInput
        value={dialerForm.values.directPhoneNumber}
        onChange={(value) => {
          dialerForm.setFieldValue("directPhoneNumber", value);
          // Clear contact selection when entering direct number
          if (value) {
            setSelectedEnduser([]);
          }
        }}
        placeholder={t("Enter phone number")}
      />

      {/* Call Type Field */}
      <div className="space-y-2">
        <Label htmlFor="call-type">{t("Call type")}</Label>
        <Select
          value={dialerForm.values.callType}
          onValueChange={(value) => dialerForm.setFieldValue("callType", value)}
        >
          <Select.Trigger id="call-type" className="h-9 w-full">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="Direct">{t("Direct")}</Select.Item>
            <Select.Item value="Conference">{t("Conference")}</Select.Item>
          </Select.Content>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          {t("Cancel")}
        </Button>
        <Button
          variant="default"
          onClick={() => dialerForm.submitForm()}
          disabled={!canPlaceCall}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {dialerForm.isSubmitting ? t("Calling...") : t("Place call")}
        </Button>
      </div>

      {/* Connection status warning */}
      {!calling.isConnected && (
        <p className="text-xs text-destructive text-center">{t("Phone service is not connected. Please wait...")}</p>
      )}
    </div>
  );
}
